import { createAsyncThunk } from '@reduxjs/toolkit';
import { getProblemDetail } from 'api/v1Helpers';
import { extractErrorMessage } from 'utils/helpers';

// Define a type for the wrapper function
type ThunkWrapperOptions = {
    successMessage?: string;
    errorMessage?: string;
    parseErrorMessage?: (error: unknown) => string;
    messageType?: string;
    /**
     * Default true. Pass false when the screen shows the error itself (e.g. an inline alert with
     * Retry): the message still arrives as the rejected payload, but notificationMiddleware gets no
     * `errorMessage` meta, so no toast duplicates it.
     */
    toastOnError?: boolean;
};

/**
 * Pass the ReturnType and ThunkArg type if need to pass data while dispatching the action e.g @Login action
 * and if no need to pass any argument then leave the ThunkArg and just pass the ReturnType type e.g @fetchMyUserInfo action
 */

export function createAsyncThunkWrapper<ReturnType, ThunkArg = void>(
    typePrefix: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    payloadCreator: (arg: any, thunkApi?: any) => Promise<ReturnType>,
    options: ThunkWrapperOptions = {}
) {
    const { successMessage, errorMessage, parseErrorMessage, messageType, toastOnError = true } = options;
    return createAsyncThunk<ReturnType, ThunkArg>(typePrefix, async (arg, thunkApi) => {
        try {
            const response = await payloadCreator(arg, thunkApi);
            // @ts-expect-error See the docs 'https://redux-toolkit.js.org/api/createAsyncThunk'
            return thunkApi.fulfillWithValue(response, { successMessage, messageType });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
        } catch (error: any) {
            let message: string | null = null;
            const hideToast = error?.config?.headers?.hideToast;
            if (errorMessage) {
                message = errorMessage;
            } else if (parseErrorMessage) {
                message = parseErrorMessage(error);
            } else if (error?.message === 'Network Error') {
                message = error.message;
            } else if (!hideToast) {
                message = handleErrorMessage(error);
            }
            // @ts-expect-error See the docs 'https://redux-toolkit.js.org/api/createAsyncThunk'
            throw thunkApi.rejectWithValue(message, { errorMessage: toastOnError ? message : undefined, messageType });
        }
    });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
const handleErrorMessage = (error: any) => {
    // Never log the raw error/AxiosError here — its `config` carries the live
    // Authorization bearer token (see apiClient's request interceptor), and console.log
    // would print it into every deployed session's browser console on every API failure.
    if (process.env.NODE_ENV !== 'production') {
        console.log('$$$$$ ~ handleErrorMessage ~ error.response?.data:', error?.response?.data ?? error?.message);
    }
    if (error?.response) {
        // if the response is invalid credentials means login is attempted so no need to call the refresh token api
        if (error?.response?.data?.statusMessage === 'invalid credentials') {
            return 'Invalid credentials';
        }
        if (error.response?.data?.code === 'SECURITY') {
            return error.response?.data?.statusMessage;
        }
        // problem+json errors from the new backend: {type, title, status, detail, errors?}.
        // Delegated so thunks with no `parseErrorMessage` render field errors identically
        // to those that have one — two copies of this read is how they drift.
        const problem = getProblemDetail(error);
        if (problem) {
            return problem;
        }
        const msg = extractErrorMessage(error.response?.data?.message || '');
        if (msg) {
            return msg;
        }
        const data = error.response?.data;
        const additionalStatusMessage = data?.contextResponse?.additionalStatusMessage;
        const status = data?.status;
        const responseMessage = data?.error;
        const errorMessage = error?.message;
        if (additionalStatusMessage) {
            return additionalStatusMessage;
        } else if (responseMessage) {
            return responseMessage;
        } else if (errorMessage) {
            return errorMessage;
        } else if (status) {
            return status;
        }
    }
    return 'Something went wrong!';
};
